import { useTranslate } from "@refinedev/core";
import { ConfigProvider, Flex, notification } from "antd";
import {
  Chapter,
  SLIDE_TYPE_TO_LAYOUT,
  TabKeys,
  TemplateState,
  TemplateType,
} from "./types";
import { generateTemplate } from "./helpers/generateTemplate";
import { useCallback, useEffect, useRef } from "react";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import styled from "styled-components";
import { StoryboardLeftPanel } from "./components/leftPanel/StoryboardLeftPanel";
import { useSaveTemplate } from "./hooks/useSaveTemplate";
import { useTemplateAutoSave } from "./hooks/useTemplateAutoSave";
import { useTemplateSetup } from "./hooks/useTemplateSetup";
import {
  SelectionSource,
  useTemplateEditorStore,
  useTemplateEditorStoreApi,
} from "./store/useTemplateEditorStore";
import { useTemplateData } from "./hooks/useTemplateData";
import { useTemplatesStatusStore } from "./store/useTemplatesStatusStore";
import { StoryboardScenes } from "./components/StoryboardScenes";
import { StoryboardHeader } from "./components/StoryboardHeader";
import { HEADER_HEIGHT } from "./constants";
import { StoryboardSlidesScenes } from "./components/StoryboardSlidesScenes";
import { useBoundStore } from "@/store";
import { useStoryboardWsStore } from "@/store/storyboardWsStore";
import { mapTemplateStateToServerTemplate } from "./helpers/mapTemplateStateToServerTemplate";
import {
  hasParameteredTextContentChanged,
  syncParameteredTextContentToSlidePlaceholders,
} from "./helpers/itemsHelper";
import { useSaveSlidesAsset } from "./hooks/useSaveSlidesAsset";
import { TTSProvider } from "@/providers/TTSProvider";
import { useEnsureBrandKitDefaultFonts } from "pages/media/brandkit/hooks/useEnsureBrandKitDefaultFonts";

const LEFT_PANEL_DEFAULT_PERCENT = 15;
const LEFT_PANEL_MIN_PERCENT = 15;
const LEFT_PANEL_MAX_PERCENT = 25;

const StyledLeftPanelGroup = styled(PanelGroup)`
  width: 100%;
  height: 100%;

  div[data-resize-handle-active="keyboard"],
  div[data-resize-handle-active="pointer"] {
    background: ${({ theme }) => theme.colorPrimary};
  }
`;

const ResizeHandleBar = styled.div`
  width: 1px;
  height: 100%;
  background: ${({ theme }) => theme.colorBorder};
  margin-inline: 1px;
`;

export const StoryboardEditor = () => {
  const {
    projectId,
    templatesData: {
      organizationId,
      brandKitId,
      storyboard,
      isFlashProject,
      primaryDocumentId,
      presenter,
    },
    // BE never auto-marks the *media* stale on any asset or template write
    // (MED-7168), so every save path here has to flag it manually. The
    // publication itself is left alone: the media hasn't actually been
    // regenerated yet, so nothing live is out of date — BE marks the
    // publication stale once the regenerated media lands (see MED-6969).
    mediaGenerationSettingsData: {
      mediaGenerationSettings,
      mediaAspectRatio,
      markMediaOutOfDate,
    },
    storyboardSections,
    isLoading: isDataLoading,
    previewData: { previewSizes },
    storyboardWithSlidesData: { setSlide, slides },
    projectTheme,
    brandKitData: { titleFont, textFont, subtitlesFont },
  } = useTemplateData();

  const { ensureDefaultFonts } = useEnsureBrandKitDefaultFonts();

  const {
    templateState,
    currentChapter,
    setCurrentChapter,
    setCurrentSlide,
    templates,
    serverTemplates,
    slideNameToTemplateIdMap,
    currentSlide,
  } = useTemplateEditorStore((state) => ({
    templateState: state.templateState,
    currentChapter: state.currentChapter,
    setCurrentChapter: state.setCurrentChapter,
    setCurrentSlide: state.setCurrentSlide,
    templates: state.templates,
    serverTemplates: state.serverTemplates,
    slideNameToTemplateIdMap: state.slideNameToTemplateIdMap,
    currentSlide: state.currentSlide,
  }));

  const {
    textStyle,
    titleStyle,
    intro: storeIntro,
    closing: storeClosing,
  } = templateState;

  const { type: view } = currentChapter;

  const { updates, resetUpdates, resetBulkUpdates, setActiveTab, setZoom } =
    useTemplatesStatusStore((state) => ({
      updates: state.updates,
      resetUpdates: state.resetTemplateUpdates,
      resetBulkUpdates: state.resetBulkTemplateUpdates,
      setActiveTab: state.setActiveTab,
      setZoom: state.setZoom,
    }));

  const setSidebarCollapsed = useBoundStore(
    (state) => state.setSidebarCollapsed
  );

  useEffect(() => {
    setSidebarCollapsed(true);
    setActiveTab(TabKeys.script);
    return () => {
      void saveBeforeLeavingRef.current();
      setSidebarCollapsed(false);
      // Reset zoom so it doesn't carry over to the theme editor (shared store)
      setZoom(1);
    };
    // to be fired only on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize storyboard WebSocket for TTS streaming
  useEffect(() => {
    if (
      projectId &&
      organizationId &&
      storyboard?.asset_id &&
      primaryDocumentId
    ) {
      useStoryboardWsStore.getState().init({
        organizationId,
        documentIds: [primaryDocumentId],
        projectId,
        storyboardId: storyboard.asset_id,
      });
    }
    return () => {
      useStoryboardWsStore.getState().disconnect();
    };
  }, [projectId, organizationId, storyboard?.asset_id, primaryDocumentId]);

  const t = useTranslate();

  const { updateTemplate } = useSaveTemplate(organizationId);
  const { saveSlidesAsset } = useSaveSlidesAsset({
    organizationId,
    projectId,
  });

  const [api, contextHolder] = notification.useNotification();

  useTemplateSetup();

  const validateAssets = useCallback(() => {
    const checkFont = () => {
      // Only require a font when the template actually has the corresponding
      // text element. Some templates use only custom texts (no subtitle/title
      // item), so textStyle/titleStyle have no font — that must not block saving.
      if (view === TemplateType.CHAPTER) {
        return !textStyle.showSubtitles || !!textStyle.fontAssetId;
      } else if (view === TemplateType.TRANSITION) {
        return !titleStyle.showTitle || !!titleStyle.fontAssetId;
      }
      return true;
    };

    const checkIntroClosingAsset = () => {
      if (view === TemplateType.INTRODUCTION) {
        return !!storeIntro.assetId;
      } else if (view === TemplateType.CLOSING) {
        return !!storeClosing.assetId;
      }
      return true;
    };

    const hasFont = checkFont();
    const hasIntroClosingAsset = checkIntroClosingAsset();

    if (!hasIntroClosingAsset) {
      api.warning({
        message: t("components.templates.index.uploadVideo", { view }),
      });

      return false;
    }

    if (!hasFont) {
      return false;
    }

    return true;
  }, [
    view,
    textStyle.showSubtitles,
    textStyle.fontAssetId,
    titleStyle.showTitle,
    titleStyle.fontAssetId,
    storeIntro.assetId,
    storeClosing.assetId,
    api,
    t,
  ]);

  const templateEditorStoreApi = useTemplateEditorStoreApi();

  const handleSave = useCallback(async () => {
    if (isFlashProject) return;
    const canSave = validateAssets();
    if (!canSave || !mediaAspectRatio) return;

    const templateState = templateEditorStoreApi.getState().templateState;
    const serverTemplate = generateTemplate(
      templateState,
      currentChapter.type,
      currentChapter.topic,
      mediaAspectRatio
    );
    const templateId = currentChapter.templateId;

    try {
      if (templateId) {
        await updateTemplate(templateId, serverTemplate);
        resetUpdates();
        await markMediaOutOfDate();
      }
    } catch (e) {
      console.debug(e);
    }
  }, [
    currentChapter,
    markMediaOutOfDate,
    mediaAspectRatio,
    resetUpdates,
    templateEditorStoreApi,
    updateTemplate,
    validateAssets,
    isFlashProject,
  ]);

  const handleSaveSlide = useCallback(async () => {
    if (!mediaAspectRatio) return;

    const templateState = templateEditorStoreApi.getState().templateState;

    // Handle intro/closing saves
    const isIntroOrClosing =
      currentChapter.type === TemplateType.INTRODUCTION ||
      currentChapter.type === TemplateType.CLOSING;

    if (isIntroOrClosing) {
      const templateId =
        currentChapter.type === TemplateType.INTRODUCTION
          ? mediaGenerationSettings?.introduction_template_id
          : mediaGenerationSettings?.closing_template_id;

      if (!templateId) return;

      const serverTemplate = generateTemplate(
        templateState,
        currentChapter.type,
        currentChapter.type === TemplateType.INTRODUCTION
          ? "Introduction"
          : "Closing",
        mediaAspectRatio
      );

      try {
        await updateTemplate(templateId, serverTemplate);
        resetUpdates();
        // An intro/closing template edit never patches the Slides asset, so BE
        // never auto-marks the media here — flag it ourselves.
        await markMediaOutOfDate();
      } catch (e) {
        console.debug(e);
      }
      return;
    }

    if (!currentSlide) return;

    const templateId = slideNameToTemplateIdMap[currentSlide];
    if (!templateId) return;

    const serverTemplate = serverTemplates?.get(templateId);
    if (!serverTemplate) return;

    const currentSlideData = slides.find((s) => s.uuid === currentSlide);
    if (!currentSlideData?.uuid) return;

    // Never save a design of a different slide type onto this slide.
    if (
      templateState.slideLayoutType &&
      templateState.slideLayoutType !==
        SLIDE_TYPE_TO_LAYOUT[currentSlideData.slide_type]
    ) {
      console.error(
        "Skipped slide save: template layout does not match the slide type",
        templateState.slideLayoutType,
        currentSlideData.slide_type
      );
      return;
    }

    const isTransitionSlide = mediaGenerationSettings?.chapters.some(
      (c) => c.transition_template_id === templateId
    );

    const mappedServerTemplate = mapTemplateStateToServerTemplate(
      currentSlide,
      templateState,
      serverTemplate,
      mediaAspectRatio,
      new Set(slides.map((s) => s.uuid)),
      !!isTransitionSlide,
      templateState.slideLayoutType && projectTheme
        ? projectTheme.layouts[templateState.slideLayoutType]?.layout
        : undefined
    );
    if (!mappedServerTemplate) return;

    // Keep the store in sync when a visible avatar forced the transition's
    // speech to FixedScript, so the UI doesn't keep showing a stale choice.
    if (
      mappedServerTemplate.speech?.type === "FixedScript" &&
      templateState.transition.speechType !== "FixedScript"
    ) {
      templateEditorStoreApi.getState().updateTemplateState({
        transition: { ...templateState.transition, speechType: "FixedScript" },
      });
    }

    try {
      await updateTemplate(templateId, mappedServerTemplate);

      // Only sync placeholders and save slides asset if text content changed
      const contentChanged = hasParameteredTextContentChanged(
        templateState.parameteredTexts,
        templateState.figure,
        currentSlideData
      );

      const slidesAssetId = contentChanged
        ? storyboard?.slides_asset_id
        : undefined;

      if (slidesAssetId) {
        const updatedSlide = syncParameteredTextContentToSlidePlaceholders(
          templateState.parameteredTexts,
          templateState.figure,
          currentSlideData
        );
        setSlide(currentSlideData.uuid, updatedSlide);

        const allSlidesToSave = slides.map((slide) =>
          slide.uuid === updatedSlide.uuid ? updatedSlide : slide
        );
        await saveSlidesAsset(slidesAssetId, allSlidesToSave);
      }

      // The styling-only path PATCHes the template alone, which BE never
      // auto-marks — the Slides PATCH above already marks it itself.
      if (!slidesAssetId) {
        await markMediaOutOfDate();
      }

      resetUpdates();
    } catch (e) {
      console.debug(e);
    }
  }, [
    currentChapter.type,
    currentSlide,
    markMediaOutOfDate,
    mediaAspectRatio,
    mediaGenerationSettings?.introduction_template_id,
    mediaGenerationSettings?.closing_template_id,
    mediaGenerationSettings?.chapters,
    serverTemplates,
    slideNameToTemplateIdMap,
    templateEditorStoreApi,
    updateTemplate,
    resetUpdates,
    slides,
    setSlide,
    storyboard?.slides_asset_id,
    saveSlidesAsset,
    projectTheme,
  ]);

  /**
   * @description Saves all templates in bulk. Since handleSave only saves the current template, we need to make sure that other templates are saved after performing bulk actions like aply to all or apply to specific chapters. Instead of saving directly after performing these actions, we pass it to the autoSave hook to cover bulk actions reverted with undo/redo actions.
   */
  const handleBulkSave = useCallback(async () => {
    if (isFlashProject) {
      // Group slides by templateId to avoid saving the same template multiple times
      const slidesByTemplateId = new Map<
        string,
        { slideId: string; slideTemplateState: TemplateState }[]
      >();

      for (const [slideId, slideTemplateState] of templates) {
        const templateId = slideNameToTemplateIdMap[slideId];
        if (!templateId) continue;

        const group = slidesByTemplateId.get(templateId) ?? [];
        group.push({ slideId, slideTemplateState });
        slidesByTemplateId.set(templateId, group);
      }

      const validSlideUuids = new Set(slides.map((s) => s.uuid));
      for (const [templateId, slideGroup] of slidesByTemplateId) {
        let accumulatedTemplate = serverTemplates?.get(templateId);
        if (!accumulatedTemplate) continue;

        const isTransitionSlide = mediaGenerationSettings?.chapters.some(
          (c) => c.transition_template_id === templateId
        );

        for (const { slideId, slideTemplateState } of slideGroup) {
          const themeLayoutItems =
            slideTemplateState.slideLayoutType && projectTheme
              ? projectTheme.layouts[slideTemplateState.slideLayoutType]?.layout
              : undefined;

          const mapped = mapTemplateStateToServerTemplate(
            slideId,
            slideTemplateState,
            accumulatedTemplate,
            mediaAspectRatio,
            validSlideUuids,
            !!isTransitionSlide,
            themeLayoutItems
          );
          if (mapped) {
            accumulatedTemplate = mapped;
          }
        }

        await updateTemplate(templateId, accumulatedTemplate);
      }
      resetBulkUpdates();
      // A bulk template PATCH never touches the Slides asset, so nothing here
      // is auto-marked by BE — flag the media.
      await markMediaOutOfDate();
      return;
    }

    for (const chapter of mediaGenerationSettings?.chapters ?? []) {
      const topic = storyboardSections.find(
        (section) => section.id === chapter.chapter_id
      )?.topic;
      if (!topic) continue;

      const chapterTemplate =
        chapter.chapter_template_id &&
        templates.get(chapter.chapter_template_id);
      if (chapterTemplate && chapter.chapter_template_id) {
        const serverTemplate = generateTemplate(
          chapterTemplate,
          TemplateType.CHAPTER,
          topic,
          mediaAspectRatio
        );
        await updateTemplate(chapter.chapter_template_id, serverTemplate);
      }

      const transitionTemplate =
        chapter.transition_template_id &&
        templates.get(chapter.transition_template_id);
      if (transitionTemplate && chapter.transition_template_id) {
        const serverTemplate = generateTemplate(
          transitionTemplate,
          TemplateType.TRANSITION,
          topic,
          mediaAspectRatio
        );
        await updateTemplate(chapter.transition_template_id, serverTemplate);
      }
    }
    resetBulkUpdates();
    // Same reasoning as the Flash branch above: a bulk template PATCH is
    // never auto-marked by BE, so flag the media.
    await markMediaOutOfDate();
  }, [
    isFlashProject,
    markMediaOutOfDate,
    mediaAspectRatio,
    mediaGenerationSettings?.chapters,
    serverTemplates,
    slideNameToTemplateIdMap,
    slides,
    storyboardSections,
    templates,
    updateTemplate,
    resetBulkUpdates,
    projectTheme,
  ]);

  const saveBeforeLeaving = useCallback(async () => {
    const { bulkUpdates, updates } = useTemplatesStatusStore.getState();
    try {
      await ensureDefaultFonts(brandKitId ?? undefined, organizationId, {
        Title: titleFont,
        Text: textFont,
        Subtitles: subtitlesFont,
      });

      if (bulkUpdates > 0) {
        await handleBulkSave();
        resetBulkUpdates();
        resetUpdates();
      } else if (updates > 0) {
        await (isFlashProject ? handleSaveSlide() : handleSave());
        resetUpdates();
      }
    } catch (error) {
      console.error("Error saving before leaving", error);
    }
  }, [
    brandKitId,
    ensureDefaultFonts,
    handleBulkSave,
    handleSave,
    handleSaveSlide,
    isFlashProject,
    organizationId,
    resetBulkUpdates,
    resetUpdates,
    subtitlesFont,
    textFont,
    titleFont,
  ]);

  // Ref to always point to the latest callback, avoiding stale closures in the unmount cleanup
  const saveBeforeLeavingRef = useRef(saveBeforeLeaving);
  useEffect(() => {
    saveBeforeLeavingRef.current = saveBeforeLeaving;
  }, [saveBeforeLeaving]);

  useTemplateAutoSave(
    isFlashProject ? handleSaveSlide : handleSave,
    handleBulkSave
  );

  const handleCardClick = useCallback(
    (chapter: Chapter, source: SelectionSource = "scene") => {
      if (updates > 0) {
        const save = isFlashProject ? handleSaveSlide : handleSave;
        save().catch((e) => {
          console.error(e);
        });
      }
      setCurrentChapter(chapter, source);
    },
    [isFlashProject, handleSave, handleSaveSlide, setCurrentChapter, updates]
  );

  const handleSlideCardClick = useCallback(
    (slideId: string, source: SelectionSource = "scene") => {
      if (updates > 0) {
        handleSaveSlide().catch((e) => {
          console.error(e);
        });
      }
      setCurrentSlide(slideId, source);
    },
    [handleSaveSlide, setCurrentSlide, updates]
  );

  const handleThumbnailChapterClick = useCallback(
    (chapter: Chapter) => handleCardClick(chapter, "thumbnail"),
    [handleCardClick]
  );

  const handleThumbnailSlideClick = useCallback(
    (slideId: string) => handleSlideCardClick(slideId, "thumbnail"),
    [handleSlideCardClick]
  );

  return (
    <TTSProvider voiceId={presenter?.voice_profile.id}>
      {contextHolder}
      {/* block UI only on first page load to make sure we have all the data */}
      <LoadingSpinner loading={isDataLoading} data-testid="templates-loading" />
      {projectId && !isDataLoading && previewSizes && (
        <ConfigProvider theme={{ token: { fontFamily: "Inter, sans-serif" } }}>
          <Flex
            style={{
              height: "100vh",
              margin: -24,
              position: "relative",
              paddingBlockStart: HEADER_HEIGHT,
              fontFamily: "Inter, sans-serif",
            }}
            tabIndex={0}
          >
            <StoryboardHeader
              onBeforeLeaving={saveBeforeLeaving}
              previewWidth={previewSizes.scene.width}
              previewHeight={previewSizes.scene.height}
            />
            <StyledLeftPanelGroup
              direction="horizontal"
              autoSaveId="storyboard-left-panel"
            >
              <Panel
                id="left-panel"
                order={1}
                defaultSize={LEFT_PANEL_DEFAULT_PERCENT}
                minSize={LEFT_PANEL_MIN_PERCENT}
                maxSize={LEFT_PANEL_MAX_PERCENT}
                style={{ display: "flex", flexDirection: "column" }}
              >
                <StoryboardLeftPanel
                  key={organizationId}
                  handleSlideCardClick={handleThumbnailSlideClick}
                  handleChapterCardClick={handleThumbnailChapterClick}
                />
              </Panel>
              <PanelResizeHandle>
                <ResizeHandleBar />
              </PanelResizeHandle>
              <Panel
                id="scenes-area"
                order={2}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {isFlashProject ? (
                  <StoryboardSlidesScenes
                    handleSlideCardClick={handleSlideCardClick}
                    handleChapterCardClick={handleCardClick}
                    previewSizes={previewSizes}
                  />
                ) : (
                  <StoryboardScenes
                    handleCardClick={handleCardClick}
                    previewSizes={previewSizes}
                  />
                )}
              </Panel>
            </StyledLeftPanelGroup>
          </Flex>
        </ConfigProvider>
      )}
    </TTSProvider>
  );
};
